import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { impactsData } from '../data/impactsData';
import { useConfiguration } from './ConfigurationContext';
import { getDefaultThreshold } from '../utils/severityUtils';

const DashboardContext = createContext();

const getBasePath = () => (process.env.PUBLIC_URL || '').replace(/\/$/, '') || '';

const getPathname = () => {
  const base = getBasePath();
  const path = window.location.pathname || '/';
  if (!base) return path;
  return path.startsWith(base) ? (path.slice(base.length) || '/') : path;
};

const parsePathname = (pathname) => {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (segments[0] === 'summary' && segments[1]) {
    return { view: 'summary', city: decodeURIComponent(segments[1]) };
  }
  if (segments[0] === 'detail' && segments[1] && segments[2]) {
    return { view: 'detail', city: decodeURIComponent(segments[1]), impactId: decodeURIComponent(segments[2]) };
  }
  return { view: 'home' };
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const { hasIndicatorData: configHasIndicatorData, isCityAvailable, hasAnyIndicatorData, isLoading: configLoading } = useConfiguration();
  
  const [currentView, setCurrentView] = useState('home'); // 'home', 'summary', 'detail'
  const [currentCity, setCurrentCity] = useState('');
  const [currentImpact, setCurrentImpact] = useState(null);
  const [currentIndicator, setCurrentIndicator] = useState('');
  const [selectedWard, setSelectedWard] = useState(null);
  const [wardDataMap, setWardDataMap] = useState({});
  const [weatherData, setWeatherData] = useState(null);
  const [currentGeoJSON, setCurrentGeoJSON] = useState(null);
  const [cityMapCenter, setCityMapCenter] = useState(null);
  const [cityMapZoom, setCityMapZoom] = useState(null);
  const [currentPowerOutageMode, setCurrentPowerOutageMode] = useState('severity');
  const [currentGroundwaterMode, setCurrentGroundwaterMode] = useState('severity');
  const [currentInformalWorkersMode, setCurrentInformalWorkersMode] = useState('severity');
  const [currentConstructionLaborMode, setCurrentConstructionLaborMode] = useState('severity');
  const [currentTransportDisruptionsMode, setCurrentTransportDisruptionsMode] = useState('severity');
  const [currentRoadClosuresMode, setCurrentRoadClosuresMode] = useState('severity');
  const [currentTrafficSignalFailuresMode, setCurrentTrafficSignalFailuresMode] = useState('severity');
  const [currentCoolingSystemFailuresMode, setCurrentCoolingSystemFailuresMode] = useState('severity');
  const [currentSchoolClosuresMode, setCurrentSchoolClosuresMode] = useState('severity');
  const [currentSchoolCoolingMode, setCurrentSchoolCoolingMode] = useState('severity');
  const [currentWaterSupplyMode, setCurrentWaterSupplyMode] = useState('severity');
  const [currentWaterTankersMode, setCurrentWaterTankersMode] = useState('severity');
  const [currentHeatDifferentialMode, setCurrentHeatDifferentialMode] = useState('severity');
  const [currentSurfaceTemperatureMode, setCurrentSurfaceTemperatureMode] = useState('severity');
  const [currentShelterOccupancyMode, setCurrentShelterOccupancyMode] = useState('severity');
  const [forecastDay, setForecastDay] = useState(new Date()); // Default to today
  const [tickerMessage, setTickerMessage] = useState(
    "The data used in this dashboard are sourced exclusively from publicly available and authoritative datasets. All datasets have been processed and analyzed for research and decision-support purposes, with appropriate attribution to their respective sources."
  );
  const [tickerWarning, setTickerWarning] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [targetIndicator, setTargetIndicator] = useState(null);
  const isRestoringFromUrlRef = useRef(false);

  // Check if indicator has data (using dynamic configuration)
  const hasIndicatorData = useCallback((city, indicatorName) => {
    return configHasIndicatorData(city, indicatorName);
  }, [configHasIndicatorData]);

  // Get impact order from localStorage
  const getImpactOrder = useCallback((city) => {
    const saved = localStorage.getItem(`impactOrder_${city}`);
    if (saved) {
      return JSON.parse(saved);
    }
    return impactsData.map(impact => impact.id);
  }, []);

  // Save impact order to localStorage
  const saveImpactOrder = useCallback((city, order) => {
    localStorage.setItem(`impactOrder_${city}`, JSON.stringify(order));
  }, []);

  // Get ordered impacts
  const getOrderedImpacts = useCallback((city) => {
    const order = getImpactOrder(city);
    const impactMap = {};
    impactsData.forEach(impact => {
      impactMap[impact.id] = impact;
    });
    
    return order
      .map(id => impactMap[id])
      .filter(impact => impact !== undefined)
      .concat(impactsData.filter(impact => !order.includes(impact.id)));
  }, [getImpactOrder]);

  // Update ticker
  const updateTicker = useCallback((msg, isWarning) => {
    setTickerMessage(msg);
    setTickerWarning(isWarning);
  }, []);

  // City is considered to have data if config says so OR we have any indicator data (keeps ticker in sync with what's shown)
  const cityHasDataForTicker = useCallback((city) => {
    return isCityAvailable(city) || hasAnyIndicatorData(city);
  }, [isCityAvailable, hasAnyIndicatorData]);

  // Restore state from URL on mount and when user uses back/forward
  useEffect(() => {
    const applyFromUrl = () => {
      const pathname = getPathname();
      const parsed = parsePathname(pathname);
      isRestoringFromUrlRef.current = true;
      if (parsed.view === 'home') {
        setCurrentCity('');
        setCurrentView('home');
        setCurrentImpact(null);
        setCurrentIndicator('');
        setSelectedWard(null);
        setTargetIndicator(null);
        updateTicker(
          "The data used in this dashboard are sourced exclusively from publicly available and authoritative datasets. All datasets have been processed and analyzed for research and decision-support purposes, with appropriate attribution to their respective sources.",
          false
        );
      } else if (parsed.view === 'summary' && parsed.city) {
        setCurrentCity(parsed.city);
        setCurrentView('summary');
        setCurrentImpact(null);
        setCurrentIndicator('');
        setSelectedWard(null);
        setTargetIndicator(null);
        const hasData = cityHasDataForTicker(parsed.city);
        if (hasData) {
          updateTicker(`Viewing data for ${parsed.city}. Sources: Publicly Available Datasets`, false);
        } else {
          updateTicker(`Data not available for ${parsed.city}, stay tuned for future updates.`, true);
        }
      } else if (parsed.view === 'detail' && parsed.city && parsed.impactId) {
        const impact = impactsData.find(d => d.id === parsed.impactId);
        if (impact) {
          setCurrentCity(parsed.city);
          setCurrentImpact(impact);
          setCurrentView('detail');
          setCurrentIndicator('');
          setSelectedWard(null);
          setTargetIndicator(null);
          const hasData = cityHasDataForTicker(parsed.city);
          if (hasData) {
            updateTicker(`Viewing data for ${parsed.city}. Sources: Publicly Available Datasets`, false);
          } else {
            updateTicker(`Data not available for ${parsed.city}, stay tuned for future updates.`, true);
          }
        }
      }
      isRestoringFromUrlRef.current = false;
    };

    applyFromUrl();

    const onPopState = () => applyFromUrl();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [updateTicker, cityHasDataForTicker]);

  // Re-apply ticker when config finishes loading so we don't stay on "Data not available" after async load
  useEffect(() => {
    if (configLoading) return;
    if (currentView !== 'summary' && currentView !== 'detail') return;
    if (!currentCity) return;
    if (cityHasDataForTicker(currentCity)) {
      updateTicker(`Viewing data for ${currentCity}. Sources: Publicly Available Datasets`, false);
    } else {
      updateTicker(`Data not available for ${currentCity}, stay tuned for future updates.`, true);
    }
  }, [configLoading, currentView, currentCity, cityHasDataForTicker, updateTicker]);

  // Navigate to home - Reset everything except priority ranking and thresholds
  const goHome = useCallback(() => {
    setCurrentCity('');
    setCurrentView('home');
    setCurrentImpact(null);
    setCurrentIndicator('');
    setSelectedWard(null);
    setWardDataMap({});
    setWeatherData(null);
    setCurrentGeoJSON(null);
    setCityMapCenter(null);
    setCityMapZoom(null);
    setCurrentPowerOutageMode('severity');
    setCurrentGroundwaterMode('severity');
    setCurrentInformalWorkersMode('severity');
    setCurrentConstructionLaborMode('severity');
    setForecastDay(new Date());
    setTargetIndicator(null);
    updateTicker(
      "Data is from publicly available sources (IMD, Municipal Open Data, Satellite Imagery). Metrics are aggregated for estimation purposes only.",
      false
    );
    const base = getBasePath();
    window.history.pushState({}, '', base ? `${base}/` : '/');
  }, [updateTicker]);

  // Select city
  const selectCity = useCallback((city) => {
    setCurrentCity(city);
    const hasData = cityHasDataForTicker(city);
    if (hasData) {
      updateTicker(`Viewing data for ${city}. Sources: Publicly Available Datasets`, false);
    } else {
      updateTicker(`Data not available for ${city}, stay tuned for future updates.`, true);
    }
    setCurrentView('summary');
    const base = getBasePath();
    window.history.pushState({}, '', base ? `${base}/summary/${encodeURIComponent(city)}` : `/summary/${encodeURIComponent(city)}`);
  }, [updateTicker, cityHasDataForTicker]);

  // Select impact (currentCity must be set already, e.g. from summary view)
  const selectImpact = useCallback((impactId, city) => {
    const impact = impactsData.find(d => d.id === impactId);
    if (!impact) return;
    setCurrentImpact(impact);
    setSelectedWard(null);
    setCurrentIndicator(''); // Clear so DetailView picks first indicator of the new impact
    setTargetIndicator(null);
    setCurrentView('detail');
    const cityForUrl = city ?? (typeof currentCity === 'string' ? currentCity : '');
    if (cityForUrl) {
      const base = getBasePath();
      window.history.pushState({}, '', base ? `${base}/detail/${encodeURIComponent(cityForUrl)}/${encodeURIComponent(impactId)}` : `/detail/${encodeURIComponent(cityForUrl)}/${encodeURIComponent(impactId)}`);
    }
  }, [currentCity]);

  // Reset ward selection
  const resetWardSelection = useCallback(() => {
    setSelectedWard(null);
    setWeatherData(null);
  }, []);

  // Get threshold for an indicator from localStorage (default from severityUtils per indicator)
  const getIndicatorThreshold = useCallback((indicatorName) => {
    const saved = localStorage.getItem(`threshold_${indicatorName}`);
    if (saved) {
      try {
        // Try to parse as JSON first (for arrays/objects)
        const parsed = JSON.parse(saved);
        // Validate groundwater threshold - ensure it's 0.5, not 9000 (old incorrect default)
        if (indicatorName === 'Groundwater stress levels' && typeof parsed === 'number' && parsed === 9000) {
          // Migrate incorrect value to correct default
          localStorage.setItem(`threshold_${indicatorName}`, '0.5');
          return 0.5;
        }
        return parsed;
      } catch (e) {
        // If not JSON, try parsing as number (legacy single threshold)
        const parsedValue = parseFloat(saved);
        // Validate groundwater threshold - ensure it's 0.5, not 9000 (old incorrect default)
        if (indicatorName === 'Groundwater stress levels' && parsedValue === 9000) {
          // Migrate incorrect value to correct default
          localStorage.setItem(`threshold_${indicatorName}`, '0.5');
          return 0.5;
        }
        return parsedValue;
      }
    }
    return getDefaultThreshold(indicatorName);
  }, []);

  // Save threshold for an indicator to localStorage
  const saveIndicatorThreshold = useCallback((indicatorName, threshold) => {
    // Store as JSON if it's an array or object, otherwise as string
    if (Array.isArray(threshold) || (typeof threshold === 'object' && threshold !== null)) {
      localStorage.setItem(`threshold_${indicatorName}`, JSON.stringify(threshold));
    } else {
      localStorage.setItem(`threshold_${indicatorName}`, threshold.toString());
    }
  }, []);

  // Get all thresholds for a city (returns map of indicatorName -> threshold)
  const getAllThresholds = useCallback(() => {
    const thresholds = {};
    impactsData.forEach(impact => {
      impact.sectors.forEach(sector => {
        sector.indicators.forEach(indicator => {
          thresholds[indicator.name] = getIndicatorThreshold(indicator.name);
        });
      });
    });
    return thresholds;
  }, [getIndicatorThreshold]);

  const value = {
    currentView,
    setCurrentView,
    currentCity,
    setCurrentCity,
    currentImpact,
    setCurrentImpact,
    currentIndicator,
    setCurrentIndicator,
    selectedWard,
    setSelectedWard,
    wardDataMap,
    setWardDataMap,
    weatherData,
    setWeatherData,
    currentGeoJSON,
    setCurrentGeoJSON,
    cityMapCenter,
    setCityMapCenter,
    cityMapZoom,
    setCityMapZoom,
    currentPowerOutageMode,
    setCurrentPowerOutageMode,
    currentGroundwaterMode,
    setCurrentGroundwaterMode,
    currentInformalWorkersMode,
    setCurrentInformalWorkersMode,
    currentConstructionLaborMode,
    setCurrentConstructionLaborMode,
    currentTransportDisruptionsMode,
    setCurrentTransportDisruptionsMode,
    currentRoadClosuresMode,
    setCurrentRoadClosuresMode,
    currentTrafficSignalFailuresMode,
    setCurrentTrafficSignalFailuresMode,
    currentCoolingSystemFailuresMode,
    setCurrentCoolingSystemFailuresMode,
    currentSchoolClosuresMode,
    setCurrentSchoolClosuresMode,
    currentSchoolCoolingMode,
    setCurrentSchoolCoolingMode,
    currentWaterSupplyMode,
    setCurrentWaterSupplyMode,
    currentWaterTankersMode,
    setCurrentWaterTankersMode,
    currentHeatDifferentialMode,
    setCurrentHeatDifferentialMode,
    currentSurfaceTemperatureMode,
    setCurrentSurfaceTemperatureMode,
    currentShelterOccupancyMode,
    setCurrentShelterOccupancyMode,
    forecastDay,
    setForecastDay,
    tickerMessage,
    tickerWarning,
    updateTicker,
    showHelpModal,
    setShowHelpModal,
    showSettingsModal,
    setShowSettingsModal,
    targetIndicator,
    setTargetIndicator,
    hasIndicatorData,
    getImpactOrder,
    saveImpactOrder,
    getOrderedImpacts,
    goHome,
    selectCity,
    selectImpact,
    resetWardSelection,
    getIndicatorThreshold,
    saveIndicatorThreshold,
    getAllThresholds
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

